import * as React from "react";
import { Link, Outlet, useLocation } from "react-router";
import { TaskIqLogo, VisionifyByline } from "@/components/Brand";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/cn";
import { FOOTER_NAV } from "./nav";
import { MarketingNav } from "./MarketingNav";

/**
 * The public site's chrome: top navigation and a footer, around an `<Outlet>`.
 *
 * **Deliberately not the console's shell.** The two are answering different
 * questions. A visitor is browsing and needs breadth across the top; a signed-in
 * administrator is working and needs a rail that stays put while the content
 * changes underneath it. Sharing one layout between them would compromise both -
 * a sidebar on a landing page wastes a quarter of the fold on navigation nobody
 * asked for, and a top-nav console makes every page a fresh hunt.
 *
 * The header is on the deep panel in both themes, and every marketing page opens
 * with a panel-toned band, so the two are one continuous surface rather than a
 * bar sitting on top of a page. The hairline only appears once scrolled, which
 * is the only moment it has anything to separate.
 */
export function MarketingShell() {
  const { status } = useAuth();
  const signedIn = status === "ready" || status === "pending" || status === "error";
  const scrolled = useScrolled(8);
  const { pathname, hash } = useLocation();

  // React Router keeps the scroll position across routes, which is right for a
  // console and wrong for a document site: following "Pricing" from halfway down
  // the home page would land halfway down Pricing. A hash gets its own case
  // rather than being left to the browser: client-side navigation never
  // triggers the browser's native jump-to-fragment (that only happens on a hard
  // load), so without this every dropdown item under "Product" would silently
  // land at the top of /product, indistinguishable from any other.
  React.useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    window.scrollTo({ top: 0 });
  }, [pathname, hash]);

  return (
    <div className="site-shell">
      <header className={cn("on-panel site-header", scrolled && "site-header--scrolled")}>
        <div className="container site-header__inner">
          <Link to="/" aria-label="TaskIQ home" className="site-header__brand">
            <TaskIqLogo tone="panel" />
          </Link>
          <MarketingNav signedIn={signedIn} />
        </div>
      </header>

      <main className="site-shell__main">
        <Outlet />
      </main>

      <footer className="on-panel site-footer">
        <div className="container site-footer__inner">
          <div className="site-footer__grid">
            <div>
              <TaskIqLogo tone="panel" />
              <p className="site-footer__blurb">
                Pre-task hazard assessment for field crews. Ninety seconds, on the phone already in
                their pocket, with or without a signal.
              </p>
              <VisionifyByline tone="panel" className="site-footer__byline" />
            </div>

            <nav aria-label="Footer" className="site-footer__nav">
              {FOOTER_NAV.map((column) => (
                <div key={column.heading}>
                  <p className="eyebrow eyebrow--sm eyebrow--panel-subtle">{column.heading}</p>
                  <ul className="site-footer__links">
                    {column.items.map((item) => {
                      const external = item.to.startsWith("http");
                      return (
                        <li key={item.to}>
                          {external ? (
                            <a
                              href={item.to}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="site-footer__link"
                            >
                              {item.label}
                            </a>
                          ) : (
                            <Link to={item.to} className="site-footer__link">
                              {item.label}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </div>

          <div className="site-footer__legal">
            <p className="site-footer__legal-text">
              © {new Date().getFullYear()} Visionify, Inc. All rights reserved.
            </p>
            <p className="site-footer__legal-text">
              Equipment detection only. TaskIQ never performs facial recognition.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** True once the page has been scrolled past `threshold` pixels. */
function useScrolled(threshold: number): boolean {
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > threshold);
    }
    onScroll();
    // Passive: this listener never calls preventDefault, and saying so lets the
    // browser keep scrolling on the compositor instead of waiting for us.
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  return scrolled;
}
